import { sameDay } from './DateUtils'
import { getLocalItem, getSessionItem, removeLocalItem, setLocalItem, setSessionItem } from './StorageUtils'

import {
  BOOKMARK_RESULT_LIMIT,
  BOOKMARK_STORAGE_NAME,
  DEFAULT_DISTANCES,
  OFFER_SEARCH_SESSION_NAME,
} from './constants/offerSearch'

import { OfferSearchView, OfferSearchDetailTypes } from './typeDefinitions/offerSearch'
import { OfferSearchAccordionFilters } from './typeDefinitions/offerSearch'
import { OfferSearchFilterTypes } from './typeDefinitions/offerSearch'

import { handleOfferSearchEventsRequest } from './requests/handleEventRequest'
import { getLocalisationCookie } from './LocalisationUtils'

/*************************************/
/*********** U25/U30 utils ***********/
/*************************************/
/**
 *  Finds current offer search view based on current URL
 * @returns current offer search view
 */
const getCurrentOfferSearchView = () => {
  let currentOfferSearchView = OfferSearchView.U25

  if (typeof window !== 'undefined') {
    const currentUrl = new URL(window.location.href)
    const viewParam = currentUrl.searchParams.get('view')
    const eventIdParam = currentUrl.searchParams.get('eventId')

    if (viewParam === 'detail' && eventIdParam) {
      currentOfferSearchView = OfferSearchView.U30
    }
  }

  return currentOfferSearchView
}

/**
 * Returns the correct search URL from the BE API data structure
 * @param {*} clientMapping
 * @param {*} clientId
 * @param {*} regionId
 * @returns the url and target of the search link
 */
const getSearchLinkDataByClient = (clientMapping, clientId, regionId) => {
  let searchUrl = ''
  let target = '_self'
  let text = ''
  let vigoTopicCategory = null
  let ctaTitle = ''
  let matchingRegion

  if (!clientMapping || !clientId || !regionId) return null

  // get correct url from BE data
  const matchingClientMapping = clientMapping.find((mapping) => mapping.clientId === clientId)

  if (matchingClientMapping?.regions?.length > 0) {
    matchingRegion = matchingClientMapping.regions.find((region) => region.regionId === regionId)
  }

  if (matchingRegion) {
    searchUrl = matchingRegion.url
    if (matchingRegion.target) {
      target = matchingRegion.target
    }
    if (matchingRegion.text) {
      text = matchingRegion.text
    }
    if (matchingRegion.vigoTopicCategory) {
      vigoTopicCategory = matchingRegion.vigoTopicCategory
    }
    if (matchingRegion.ctaTitle) {
      ctaTitle = matchingRegion.ctaTitle
    }
  } else if (matchingClientMapping) {
    searchUrl = matchingClientMapping.defaultUrl
    if (matchingClientMapping.target) {
      target = matchingClientMapping.target
    }
    if (matchingClientMapping.text) {
      text = matchingClientMapping.text
    }
    if (matchingClientMapping.ctaTitle) {
      ctaTitle = matchingClientMapping.ctaTitle
    }
    if (matchingClientMapping.vigoTopicCategory) {
      vigoTopicCategory = matchingClientMapping.vigoTopicCategory
    }
  }

  return { url: searchUrl, target, text, ctaTitle, vigoTopicCategory }
}

/**
 * Returns third party link object matching client id and optionally region id
 * @param {array} thirdPartyLinks
 * @param {number} clientId
 * @param {number} regionId
 * @returns link object from third party links array
 */
const getThirdPartyLinkByClient = (thirdPartyLinks, clientId, regionId) => {
  return thirdPartyLinks?.find((linkData) => {
    const linkIsRegional = linkData.regionId !== undefined && linkData.regionId !== null

    let regionMatches = true
    let clientMatches = linkData.clientId === clientId

    if (linkIsRegional) {
      regionMatches = linkData.regionId === regionId
    }

    return regionMatches && clientMatches
  })
}

/**
 * Returns the correct search URL from the BE API data structure
 * @param {*} clientMapping
 * @param {*} clientId
 * @returns the url and target of the search link
 */
const getHintTipByClient = (clientMapping, clientId) => {
  if (!clientMapping || !clientId) return null

  const matchingClientMapping = clientMapping.find((mapping) => mapping.clientId === clientId)

  if (!matchingClientMapping) return null

  if (!matchingClientMapping.tip) return null

  return matchingClientMapping.tip
}

const getOfferSearchDetailType = (child) => {
  switch (true) {
    case !!child && child.length > 1:
      return OfferSearchDetailTypes.MANY_CHILDREN
    case !!child && !!child.length:
      return OfferSearchDetailTypes.ONE_CHILDREN
    default:
      return OfferSearchDetailTypes.NO_CHILDREN
  }
}

/**
 * @param {object} event
 * @returns {array<null|number>}
 */
const getEventDurationAndQuantity = (event) => {
  const detailType = getOfferSearchDetailType(event.child_events)
  let eventQuantity, duration

  if (detailType === OfferSearchDetailTypes.NO_CHILDREN) {
    eventQuantity = event.event_quantity
    duration = event.duration
  } else if (detailType === OfferSearchDetailTypes.ONE_CHILDREN) {
    if (event.duration === event.child_events[0]?.duration) {
      duration = event.duration
      eventQuantity = event.event_quantity + event.child_events[0]?.event_quantity
    } else {
      duration = null
      eventQuantity = event.event_quantity + event.child_events[0]?.event_quantity
    }
  } else if (detailType === OfferSearchDetailTypes.MANY_CHILDREN) {
    const mainDuration = event.duration
    const childDurations = event.child_events.map((child) => child.duration)
    const allDurations = [mainDuration, ...childDurations]
    const allDurationsAreEqual = allDurations.every((duration) => duration === mainDuration)
    if (allDurationsAreEqual) {
      duration = mainDuration
    } else {
      duration = null
    }

    const mainEventQuantity = event.event_quantity
    const childEventQuantities = event.child_events.map((child) => child.event_quantity)
    eventQuantity = mainEventQuantity + childEventQuantities.reduce((a, b) => a + b, 0)
  }

  return [eventQuantity, duration]
}

/**
 *
 * @param {object} event
 * @returns {null|Requireable<string>|*}
 */
const getOfferSearchContact = (event) => {
  switch (true) {
    case !!event.displayed_contact:
      return event.displayed_contact
    case !!event.training_contact:
      return event.training_contact
    default:
      return null
  }
}

/**
 * @param {object} event
 * @param {string} type
 * @returns {string|null}
 */
const getOfferSearchContactByType = (event, type) => {
  if (!event) return null

  switch (type) {
    case OfferSearchDetailTypes.MANY_CHILDREN:
      let contacts = event.child_events.map((child) => getOfferSearchContact(child)).filter((child) => !!child)

      contacts = Array.from(new Set(contacts))

      return contacts.join(', ')
    case OfferSearchDetailTypes.ONE_CHILDREN:
      return getOfferSearchContact(event.child_events[0])
    default:
      return getOfferSearchContact(event)
  }
}

/**
 * @param {array} dates
 * @returns {boolean}
 */
const shouldRenderEventDates = (dates) => {
  if (dates?.length === 1) {
    if (dates[0].start_date === dates[0].end_date) {
      return true
    }
  }

  return dates?.length > 1
}

/**
 * Returns a distance-string for the given id
 *
 * @param id to get metaData for
 * @param metaData object wih all metaData grouped by id
 * @param prefix prefix for distance e.g. 'ca.'
 * @param unit optional unit for the returning distance string
 * @param decimalPlace
 * @returns {null|string}
 */
export const getDistanceById = (id, metaData, prefix = '', unit = 'km', decimalPlace = 2) => {
  if (metaData) {
    let distance = parseFloat(metaData[id]?.distance)
    distance = parseFloat(distance.toFixed(decimalPlace))

    return isNaN(distance) ? null : `${prefix}${distance.toLocaleString('de-DE')} ${unit}`
  }

  return null
}

/**
 * @param {object} event
 * @param {object} distances
 * @returns {object}
 */
const getClosestLocationForEvent = (event, distances) => {
  let minDistance = Infinity
  let minID = undefined
  let minEvent = undefined

  if (!event || !distances) {
    return { minDistance: undefined, minEvent: undefined }
  }

  for (let key in distances) {
    if (parseFloat(distances[key].distance) < minDistance) {
      minDistance = parseFloat(distances[key].distance)
      minID = parseFloat(key)
    }
  }

  // use helper function to find the corresponding event
  minEvent = findEventById(event, minID)

  if (minDistance === Infinity || minID === undefined || minEvent === null) {
    return { minDistance: undefined, minEvent: undefined }
  }

  minDistance = parseFloat(minDistance.toFixed(2))
  minDistance = `${minDistance.toLocaleString('de-DE')} km`

  return { minDistance, minEvent }
}

const findEventById = (event, id) => {
  // If the main event has the same ID, we return it
  if (event.id === id) {
    return event
  }

  // If the event has children, we search through them
  if (event.child_events) {
    for (const child of event.child_events) {
      if (child.id === id) {
        return child
      }
    }
  }

  return null
}

/*************************************/
/****** OfferSearchFilter utils ******/
/*************************************/
/**
 * Initialise an array which holds the state and data of all available filter making it the single source of truth for the filters
 * Object example:
 * [
 *  {
 *   type: 'topic_categories',
 *   state: [12, 3],
 *   items: [],
 *   title: "title"
 *  },
 *  {
 *   type: 'period',
 *   state: ['2020-01-01', null],
 *   start_date_from: {
 *      title: 'Kursbeginn',
 *      placeholder: "ab heute"
 *      tooltip: ""
 *   },
 *   end_date_to: {
 *      title: "Kursende",
 *      placeholder: "TT.MM.JJJJ",
 *      tooltip: ""
 *   }
 *  }
 * ]
 **/
/**
 * @param {object} filter
 * @param {int} presetCategories
 * @param {int} presetTopics
 * @param {int} searchMode
 * @param {boolean} hasLocation
 * @returns {Array<object>}
 */
const initialiseFiltersState = (filter, presetCategories, presetTopics, searchMode, hasLocation, defaultSorting) => {
  let initiallyAppliedFilters = []

  if (!filter) return initiallyAppliedFilters

  Object.keys(filter).forEach((filterType) => {
    let initialState
    let data

    switch (filterType) {
      case OfferSearchFilterTypes.TOPIC_CATEGORIES:
      case OfferSearchFilterTypes.TOPICS:
        // if both TOPICS and TOPIC_CATEGORIES come with U25 API, then only show TOPICS filter; both filters should never be visible at the same time
        if (
          filter[OfferSearchFilterTypes.TOPIC_CATEGORIES]?.length > 1 &&
          filter[OfferSearchFilterTypes.TOPICS]?.length > 1 &&
          filterType === OfferSearchFilterTypes.TOPIC_CATEGORIES
        ) {
          break
        }

        if (filter[filterType]?.length > 1) {
          let presetIds

          if (filterType === OfferSearchFilterTypes.TOPIC_CATEGORIES) {
            presetIds = presetCategories
          } else {
            presetIds = presetTopics
          }

          if (presetIds?.length > 0) {
            // only hold preset ids that also come from BE API
            initialState = presetIds.filter((presetId) => filter[filterType].find((item) => item.id === presetId))
          } else {
            initialState = []
          }
        }

        break

      case OfferSearchFilterTypes.PERIOD:
        initialState = []
        break

      case OfferSearchFilterTypes.STATE_NOT:
        initialState = filter[filterType].value
        break

      case OfferSearchFilterTypes.AUDIENCES:
        if (filter[filterType]?.length > 1) {
          initialState = []
        }
        break

      case OfferSearchFilterTypes.EVENT_TYPES:
        initialState = { day_span: [], training_types: [] }
        break

      case OfferSearchFilterTypes.SORTING:
        const firstOption = filter[filterType].options[0]
        const distanceOption = filter[filterType].options.find((option) => option.value === 'distance')
        const dateOption = filter[filterType].options.find((option) => option.value === 'start_date')

        // check if searchMode.default_sorting has a value
        if (!!defaultSorting) {
          if (defaultSorting === 'distance') {
            initialState = JSON.stringify({ distance: distanceOption.direction })
          } else if (defaultSorting === 'start_date') {
            initialState = JSON.stringify({ start_date: dateOption.direction })
          }
        } else {
          if (hasLocation) {
            initialState = JSON.stringify({ distance: distanceOption.direction })
          } else {
            if (firstOption.value === 'start_date') {
              initialState = JSON.stringify({ start_date: dateOption.direction })
            } else {
              initialState = JSON.stringify({ [firstOption.value]: firstOption.direction })
            }
          }
        }
        break

      case OfferSearchFilterTypes.ONLINE_TYPE:
        initialState = 'allCourses'
        break

      default:
        break
    }

    // add title for accordion filters -> they don't come from U25 API
    if (OfferSearchAccordionFilters.find((accordionFilter) => accordionFilter.type === filterType)) {
      if (filterType === OfferSearchFilterTypes.PERIOD || filterType === OfferSearchFilterTypes.ONLINE_TYPE) {
        data = {
          items: { ...filter[filterType] },
          title: OfferSearchAccordionFilters.find((filter) => filter.type === filterType).title,
        }
      } else {
        data = {
          items: filter[filterType],
          title: OfferSearchAccordionFilters.find((filter) => filter.type === filterType).title,
        }
      }
    } else {
      data = { ...filter[filterType] }
    }

    if (initialState !== undefined) {
      initiallyAppliedFilters.push({ type: filterType, state: initialState, ...data })
    }
  })

  let searchModeFilter = null
  const offerSearchSession = getSessionItem(OFFER_SEARCH_SESSION_NAME) || {}

  if (!!offerSearchSession && !!searchMode) {
    searchModeFilter = offerSearchSession[searchMode]?.filter
  }

  if (!!searchModeFilter) {
    // filters that can be overwritten via url params AND have array values
    const arrayOverwriteTypes = ['topic_categories', 'topics']

    initiallyAppliedFilters.forEach(({ type }, index) => {
      const sessionFilterType = searchModeFilter.find((f) => f.type === type)

      // if the filter type is one of the arrayOverwriteTypes and the initial filter has a non-empty state
      // then ignore the filter state from the session
      if (arrayOverwriteTypes.includes(type) && initiallyAppliedFilters[index]?.state?.length) {
        return
      }

      if (
        sessionFilterType &&
        ((!Array.isArray(sessionFilterType.state) && sessionFilterType?.state) ||
          (Array.isArray(sessionFilterType.state) && sessionFilterType.state.length))
      ) {
        initiallyAppliedFilters[index].state = sessionFilterType.state
      }
    })
  } else if (!offerSearchSession[searchMode]) {
    offerSearchSession[searchMode] = {
      filter: null,
    }
  } else {
    offerSearchSession[searchMode] = {
      ...offerSearchSession[searchMode],
      filter: null,
    }
  }

  offerSearchSession[searchMode].filter = initiallyAppliedFilters

  setSessionItem(OFFER_SEARCH_SESSION_NAME, offerSearchSession)

  return initiallyAppliedFilters
}

const initialisePageNumber = (searchMode) => {
  if (!searchMode) {
    return 1
  }
  const offerSearchSession = getSessionItem(OFFER_SEARCH_SESSION_NAME) || {}
  const searchModeSession = offerSearchSession[searchMode]

  if (searchModeSession?.page) {
    return searchModeSession.page
  }

  return 1
}

const initialiseRadius = (searchMode, presetRadius) => {
  if (presetRadius) {
    return presetRadius
  }

  if (!searchMode) {
    return DEFAULT_DISTANCES[3].value
  }

  const offerSearchSession = getSessionItem(OFFER_SEARCH_SESSION_NAME) || {}
  const searchModeSession = offerSearchSession[searchMode]

  if (searchModeSession?.radius) {
    return searchModeSession.radius
  }

  return DEFAULT_DISTANCES[3].value
}

const initialiseQuery = (searchMode, presetQuery) => {
  if (presetQuery) {
    return presetQuery
  }

  if (!searchMode) {
    return ''
  }

  const offerSearchSession = getSessionItem(OFFER_SEARCH_SESSION_NAME) || {}
  const searchModeSession = offerSearchSession[searchMode]

  if (searchModeSession?.query) {
    return searchModeSession.query
  }

  return ''
}

/**
 * Returns a list of all filters which will be rendered in an accordion
 * @param {*} currentFilters
 * @returns accordion filters
 */
const getAvailableAccordionFilters = (currentFilters) => {
  return currentFilters.filter((filter) =>
    OfferSearchAccordionFilters.find((accordionFilter) => accordionFilter.type === filter.type)
  )
}

/**
 * Check if any filters in filter menu are active
 * @param {*} currentFilters current filter state object
 * @param {*} dateOfPageLoad date of page load
 * @returns true if any filters are active, false otherwise
 */
const checkForActiveFilters = (currentFilters, dateOfPageLoad, defaultSorting) => {
  for (const currentFilterItem of currentFilters) {
    switch (currentFilterItem.type) {
      case OfferSearchFilterTypes.PERIOD:
        const startFilterDate = new Date(currentFilterItem.state[0])
        const endFilterDate = new Date(currentFilterItem.state[1])
        if (!sameDay(startFilterDate, dateOfPageLoad) || endFilterDate.getTime()) {
          return true
        }
        break

      case OfferSearchFilterTypes.TOPIC_CATEGORIES:
      case OfferSearchFilterTypes.TOPICS:
      case OfferSearchFilterTypes.AUDIENCES:
        if (currentFilterItem.state?.length > 0) return true
        break

      case OfferSearchFilterTypes.STATE_NOT:
        if (currentFilterItem.state?.length === 0) return true
        break

      case OfferSearchFilterTypes.EVENT_TYPES:
        if (currentFilterItem.state.day_span?.length > 0 || currentFilterItem.state.training_types?.length > 0)
          return true
        break

      case OfferSearchFilterTypes.SORTING:
        const sortingFilter = currentFilters.find((filter) => filter.type === OfferSearchFilterTypes.SORTING)
        if (sortingFilter && sortingFilter.options.length > 0) {
          const firstOption = sortingFilter.options[0].value
          const isDefaultSorting = currentFilterItem.state.includes(defaultSorting)
          const isFirstOption = currentFilterItem.state.includes(firstOption)
          const isSortingActive = !!defaultSorting ? isDefaultSorting : isFirstOption
          if (!isSortingActive) {
            return true
          }
        }
        break

      case OfferSearchFilterTypes.ONLINE_TYPE:
        const defaultOption = currentFilterItem.state.includes('allCourses') || currentFilterItem.state === ''
        if (!defaultOption) return true
        break

      default:
        return false
    }
  }

  return false
}

/**
 * Get filter count of currently applied filters
 * @param {*} currentFilters current filter state object
 * @param {*} dateOfPageLoad date of page load
 * @returns count of active filters
 */
const getActiveFilterCount = (currentFilters, dateOfPageLoad, defaultSorting) => {
  let activeFilterCount = 0

  for (const currentFilterItem of currentFilters) {
    switch (currentFilterItem.type) {
      case OfferSearchFilterTypes.PERIOD:
        const startFilterDate = new Date(currentFilterItem.state[0])
        const endFilterDate = new Date(currentFilterItem.state[1])

        if (!sameDay(startFilterDate, dateOfPageLoad) || endFilterDate.getTime()) {
          activeFilterCount++
        }
        if (!sameDay(startFilterDate, dateOfPageLoad) && endFilterDate.getTime()) {
          activeFilterCount++
        }
        break

      case OfferSearchFilterTypes.TOPIC_CATEGORIES:
      case OfferSearchFilterTypes.TOPICS:
      case OfferSearchFilterTypes.AUDIENCES:
        if (currentFilterItem.state.length > 0) activeFilterCount += currentFilterItem.state.length
        break

      case OfferSearchFilterTypes.EVENT_TYPES:
        if (currentFilterItem.state.day_span?.length > 0 || currentFilterItem.state.training_types?.length > 0)
          activeFilterCount += currentFilterItem.state.day_span.length + currentFilterItem.state.training_types.length
        break

      case OfferSearchFilterTypes.STATE_NOT:
        if (currentFilterItem.state?.length === 0) activeFilterCount++
        break

      case OfferSearchFilterTypes.SORTING:
        const sortingFilter = currentFilters.find((filter) => filter.type === OfferSearchFilterTypes.SORTING)
        if (sortingFilter && sortingFilter.options.length > 0) {
          const firstOption = sortingFilter.options[0].value
          const isDefaultSorting = currentFilterItem.state.includes(defaultSorting)
          const isFirstOption = currentFilterItem.state.includes(firstOption)
          const isSortingActive = !!defaultSorting ? isDefaultSorting : isFirstOption
          if (!isSortingActive) {
            activeFilterCount++
          }
        }

        break

      case OfferSearchFilterTypes.ONLINE_TYPE:
        const defaultOption = currentFilterItem.state.includes('allCourses') || currentFilterItem.state === ''
        if (!defaultOption) activeFilterCount++
        break

      default:
        break
    }
  }

  return activeFilterCount
}

/**
 * Returns if a certain filter exists based on its type
 * @param {*} currentFilters Current filter state object
 * @param {*} filterType filter type to check for
 * @returns existence of filter
 */
const filterExists = (currentFilters, filterType) => {
  return !!currentFilters.find((filter) => filter.type === filterType)
}

/**
 * Returns all data of a certain filter based on its type
 * @param {*} currentFilters Current filter state object
 * @param {*} filterType filter type to look for
 * @returns data of filter
 */
const getFilter = (currentFilters, filterType) => {
  return currentFilters.find((filter) => filter.type === filterType)
}

const resetFilterState = (currentFilters, dateOfPageLoad, defaultSorting) => {
  const newFilterState = JSON.parse(JSON.stringify(currentFilters))

  for (const newFilterStateItem of newFilterState) {
    switch (newFilterStateItem.type) {
      case OfferSearchFilterTypes.PERIOD:
        if (dateOfPageLoad instanceof Date) newFilterStateItem.state[0] = dateOfPageLoad.toISOString()
        newFilterStateItem.state[1] = null
        break

      case OfferSearchFilterTypes.TOPICS:
      case OfferSearchFilterTypes.TOPIC_CATEGORIES:
      case OfferSearchFilterTypes.AUDIENCES:
        newFilterStateItem.state = []
        break

      case OfferSearchFilterTypes.STATE_NOT:
        newFilterStateItem.state = newFilterStateItem?.value
        break

      case OfferSearchFilterTypes.EVENT_TYPES:
        newFilterStateItem.state = { day_span: [], training_types: [] }
        break

      case OfferSearchFilterTypes.SORTING:
        const firstOption = newFilterStateItem.options[0]
        const distanceOption = newFilterStateItem.options.find((option) => option.value === 'distance')
        const dateOption = newFilterStateItem.options.find((option) => option.value === 'start_date')

        // check if searchMode.default_sorting has a value
        if (!!defaultSorting) {
          if (defaultSorting === 'distance') {
            newFilterStateItem.state = JSON.stringify({ distance: distanceOption.direction })
          } else if (defaultSorting === 'start_date') {
            newFilterStateItem.state = JSON.stringify({ start_date: dateOption.direction })
          }
        } else {
          if (firstOption.value === 'start_date') {
            newFilterStateItem.state = JSON.stringify({ start_date: dateOption.direction })
          } else {
            newFilterStateItem.state = JSON.stringify({ [firstOption.value]: firstOption.direction })
          }
        }
        break

      case OfferSearchFilterTypes.ONLINE_TYPE:
        newFilterStateItem.state = 'allCourses'
        break

      default:
        break
    }
  }

  return newFilterState
}

/**
 * Returns the filter type and state based on the URL parameter
 * @param {*} currentFilters Current filter state object
 * @param {*} param URL parameter
 * @param {*} paramValue URL parameter value
 * @returns filter type and state
 */
const handleUrlParamsForFilter = (currentFilters, param, paramValue) => {
  let regex = /\[([^\]]+)\]/

  let match = param.match(regex)
  if (match) {
    // match[1] contains the value inside the square brackets (filter[online_type] -> online_type)
    let filterType = match[1]
    let validParam = false

    switch (filterType) {
      case OfferSearchFilterTypes.ONLINE_TYPE:
        // check if URLParam is an option of a filter and if it has the value as option
        validParam = filterHasTypeAndOption(currentFilters, filterType, paramValue)

        break
      default:
        break
    }

    if (validParam) {
      return { filterType: filterType, filterState: paramValue }
    }
  }
  return { filterType: null, filterState: null }
}

const filterHasTypeAndOption = (array, typ, wert) => {
  return array.some(
    (element) =>
      element.type === typ && Array.isArray(element.options) && element.options.some((option) => option.value === wert)
  )
}

/**
 * @param {number} searchMode
 * @param {object} values
 */
const setOfferSessionItem = (searchMode, values) => {
  if (!searchMode || !values) {
    return
  }

  let offerSearchSession = getSessionItem(OFFER_SEARCH_SESSION_NAME) || {}
  const searchModeSession = offerSearchSession[searchMode]

  offerSearchSession = {
    ...offerSearchSession,
    [searchMode]: {
      ...searchModeSession,
      ...values,
    },
  }

  setSessionItem(OFFER_SEARCH_SESSION_NAME, offerSearchSession)
}

const checkForRenderSorting = (currentFilters, isOnlineCourse) => {
  if (filterExists(currentFilters, OfferSearchFilterTypes.SORTING) && !isOnlineCourse) {
    return true
  }
  return false
}

/********************* OfferSearchBookmark utils ***************************/

/**
 * @param {array<number>} eventIds
 * @param {number} key
 */
const setOfferBookmark = (eventIds, key) => {
  if (!key) {
    return
  }

  let bookmarks = getLocalItem(BOOKMARK_STORAGE_NAME)

  if (!eventIds || !eventIds.length) {
    if (bookmarks && bookmarks[key]) {
      delete bookmarks[key]

      if (Object.keys(bookmarks).length) {
        setLocalItem(BOOKMARK_STORAGE_NAME, bookmarks)
      } else {
        removeLocalItem(BOOKMARK_STORAGE_NAME)
      }
    }
  } else {
    const eventIdsByKey = { [key]: eventIds }

    if (bookmarks) {
      bookmarks = {
        ...bookmarks,
        ...eventIdsByKey,
      }
    } else {
      bookmarks = eventIdsByKey
    }

    setLocalItem(BOOKMARK_STORAGE_NAME, bookmarks)
  }
}

/**
 * @param {number} key
 * @returns {null|array<number>}
 */
const getOfferBookmark = (key) => {
  if (typeof window !== 'undefined' && !!key) {
    const bookmarks = getLocalItem(BOOKMARK_STORAGE_NAME)

    if (bookmarks) {
      if (bookmarks && bookmarks[key]) {
        return bookmarks[key]
      }
    }
  }

  return null
}

const getOfferBookmarkFromApi = ({ searchModeId, url, authorisationKey, setErrorOccurred, callbackResult }) => {
  if (!searchModeId) return

  const eventIds = getOfferBookmark(searchModeId)
  const params = [
    { name: 'search_mode', value: searchModeId },
    { name: 'limit', value: BOOKMARK_RESULT_LIMIT },
  ]

  if (!eventIds?.length) {
    callbackResult({ result: null, bookmarks: [] })
  } else {
    params.push({ name: 'filter[id]', value: `[${eventIds.join(',')}]` })

    const localisationCookieData = getLocalisationCookie()
    const clientId = localisationCookieData?.client?.id

    if (clientId) {
      handleOfferSearchEventsRequest(
        url,
        authorisationKey,
        clientId || 0,
        null,
        params,
        (result) => {
          const newEventIds = result.events.map((event) => event.id)

          callbackResult({ result, bookmarks: newEventIds ? newEventIds : [] })
        },
        () => {
          if (setErrorOccurred) setErrorOccurred(true)
        }
      )
    }
  }
}

/*************************************/
/************ Y073 utils *************/
/*************************************/

/**
 * Returns next higher radius of DEFAULT_DISTANCES; if radius is null or if there is no next higher radius, null gets returned
 * @param {*} radius radius from which to get next higher radius
 * @returns next higher radius
 */
const getNextHigherRadius = (radius) => {
  let nextHigherRadius

  if (!radius) return null

  const currentRadiusIndex = DEFAULT_DISTANCES.findIndex((distance) => distance.value === radius)

  if (currentRadiusIndex !== -1 && currentRadiusIndex + 1 < DEFAULT_DISTANCES.length) {
    nextHigherRadius = DEFAULT_DISTANCES[currentRadiusIndex + 1].value
  }

  return nextHigherRadius
}

export {
  getCurrentOfferSearchView,
  getSearchLinkDataByClient,
  getThirdPartyLinkByClient,
  getHintTipByClient,
  initialiseFiltersState,
  initialisePageNumber,
  initialiseRadius,
  initialiseQuery,
  checkForActiveFilters,
  getActiveFilterCount,
  resetFilterState,
  getAvailableAccordionFilters,
  getFilter,
  filterExists,
  handleUrlParamsForFilter,
  setOfferSessionItem,
  checkForRenderSorting,
  setOfferBookmark,
  getOfferBookmark,
  getOfferBookmarkFromApi,
  getNextHigherRadius,
  getOfferSearchDetailType,
  getOfferSearchContactByType,
  getEventDurationAndQuantity,
  getClosestLocationForEvent,
  shouldRenderEventDates,
}
